/**
 * TicketTape — shared design primitives for DashFlow /login and /register.
 *
 * Aesthetic direction: "dot-matrix shipping label + bodega receipt".
 *
 *   - Inter everywhere (NO Fraunces, NO Plus Jakarta).
 *   - Indigo #1e40af primary, yellow #F6D058 accent, cream #f4efe6 left panel.
 *   - Kicker chips styled as barcode/SKU blocks (mono uppercase, letterspacing).
 *   - SVG dotted rule dividers (ink at 40% opacity, 2px dots, 8px apart).
 *   - Testimonials as receipt rows with cut-edge dotted top/bottom + soft-squared
 *     avatar thumbnails + hover "VERIFIED" stamp.
 *   - Indigo "swing tag" swatches on the right edge of the left panel.
 *
 * All props are presentational; integration (loginAsync, googleAuthAsync, etc.)
 * lives in Login.tsx / Register.tsx so this file stays side-effect free.
 */
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';

/* ───────────────────── Tokens ───────────────────── */

export const INTER_STACK =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const MONO_STACK =
  '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export const INDIGO = '#384df4';
export const INDIGO_DARK = '#2c3fd0';
export const INDIGO_TINT = '#e0e7ff';
export const YELLOW = '#F6D058';
export const CREAM = '#f4efe6';
export const CREAM_DEEP = '#ece4d3';
export const INK = '#0f172a';
export const INK_SOFT = '#475569';

/* ───────────────────── Dotted SVG divider ───────────────────── */
/**
 * Horizontal dotted rule: 2px ink dots every 8px, opacity 0.4.
 * Use for "cut-edge" lines on receipt rows, dividers between sections,
 * and the "o con email" label divider.
 */
export interface DottedRuleProps {
  opacity?: number;
  color?: string;
  height?: number;
  my?: number | string;
  label?: string;
}

export const DottedRule: React.FC<DottedRuleProps> = ({
  opacity = 0.4,
  color = INK,
  height = 2,
  my = 0,
  label,
}) => {
  // Build dashed border using repeating-linear-gradient (fast, no SVG per pixel).
  const dottedBg = `repeating-linear-gradient(
    to right,
    ${color} 0,
    ${color} 2px,
    transparent 2px,
    transparent 8px
  )`;

  if (!label) {
    return (
      <Box
        aria-hidden
        sx={{
          width: '100%',
          height: `${height}px`,
          my,
          backgroundImage: dottedBg,
          opacity,
        }}
      />
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        my,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        color: INK_SOFT,
      }}
    >
      <Box
        sx={{
          flex: 1,
          height: `${height}px`,
          backgroundImage: dottedBg,
          opacity,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontFamily: MONO_STACK,
          fontSize: 11,
          letterSpacing: '0.24em',
          textTransform: 'uppercase',
          color: INK_SOFT,
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: `${height}px`,
          backgroundImage: dottedBg,
          opacity,
        }}
      />
    </Box>
  );
};

/* ───────────────────── SKU Kicker ─────────────────────
 * A "barcode-ish" block: mono uppercase, letterspacing, cream bg, 1px ink border.
 * Ex: "SKU · AUTH-042", "LOTE · REGISTER".
 */
export interface SkuKickerProps {
  label: string;
  bg?: string;
  borderColor?: string;
  color?: string;
}

export const SkuKicker: React.FC<SkuKickerProps> = ({
  label,
  bg = CREAM,
  borderColor = INK,
  color = INK,
}) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.25,
      py: 0.5,
      border: `1px solid ${borderColor}`,
      bgcolor: bg,
      fontFamily: MONO_STACK,
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: '0.24em',
      textTransform: 'uppercase',
      color,
      lineHeight: 1,
      borderRadius: '2px',
    }}
  >
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 6,
        height: 6,
        bgcolor: color,
        opacity: 0.9,
        display: 'inline-block',
      }}
    />
    {label}
  </Box>
);

/* ───────────────────── BrandMark ─────────────────────
 * "DashFlow" wordmark with yellow dot accent. Works on both cream (dark text)
 * and indigo (light text). Used top-left of both panels.
 */
export const BrandMark: React.FC<{ color?: string; size?: 'sm' | 'md' }> = ({
  color = INK,
  size = 'md',
}) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
    <Typography
      sx={{
        fontFamily: INTER_STACK,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontSize: size === 'md' ? 22 : 18,
        color,
        lineHeight: 1,
      }}
    >
      DashFlow
    </Typography>
    <Box
      sx={{
        width: 6,
        height: 6,
        bgcolor: YELLOW,
        borderRadius: '1px',
        display: 'inline-block',
      }}
    />
  </Box>
);

/* ───────────────────── Swing tags ─────────────────────
 * Decorative "price tag" swatches on the right edge of the left panel.
 * Each tag is an indigo rectangle with a hole + string that cascades from top.
 */
export const SwingTags: React.FC = () => {
  const reducedMotion = useReducedMotion() ?? false;
  const tags = [
    { top: '14%', label: 'LOT-241', bg: INDIGO, color: '#fff' },
    { top: '38%', label: 'QTY·72', bg: YELLOW, color: INK },
    { top: '62%', label: 'OK · CBA', bg: INK, color: '#fff' },
  ];
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: 56,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {tags.map((t, i) => (
        <motion.div
          key={t.label}
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -40, rotate: -14 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, rotate: -8 }}
          transition={{ duration: 0.7, delay: 0.8 + i * 0.15, ease: [0.2, 0.8, 0.2, 1] }}
          style={{
            position: 'absolute',
            top: t.top,
            right: -14,
            transformOrigin: 'top right',
          }}
        >
          {/* string */}
          <Box
            sx={{
              position: 'absolute',
              top: -24,
              right: 12,
              width: 1,
              height: 26,
              bgcolor: INK,
              opacity: 0.4,
            }}
          />
          {/* tag */}
          <Box
            sx={{
              position: 'relative',
              bgcolor: t.bg,
              color: t.color,
              fontFamily: MONO_STACK,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              px: 1,
              py: 0.75,
              borderRadius: '2px',
              // chevron left edge — tag shape
              clipPath:
                'polygon(12px 0, 100% 0, 100% 100%, 12px 100%, 0 50%)',
              pl: 2,
              minWidth: 70,
              boxShadow: '0 6px 14px rgba(15,23,42,0.18)',
            }}
          >
            {/* hole */}
            <Box
              sx={{
                position: 'absolute',
                left: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: CREAM,
                border: `1px solid ${INK}`,
              }}
            />
            <Box component="span" sx={{ ml: 1 }}>
              {t.label}
            </Box>
          </Box>
        </motion.div>
      ))}
    </Box>
  );
};

/* ───────────────────── Testimonial (receipt row) ───────────────────── */

export interface TestimonialRowProps {
  handle: string;
  avatarSeed: number;
  quote: string;
  index: number;
  reducedMotion: boolean;
}

export const TestimonialRow: React.FC<TestimonialRowProps> = ({
  handle,
  avatarSeed,
  quote,
  index,
  reducedMotion,
}) => {
  return (
    <motion.div
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 18 }}
      animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      transition={{
        duration: 0.55,
        delay: 1.0 + index * 0.15,
        ease: [0.22, 0.8, 0.2, 1],
      }}
      style={{ position: 'relative' }}
    >
      <Box
        className="receipt-row"
        sx={{
          position: 'relative',
          py: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          // cut-edge dotted borders top + bottom
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            height: '2px',
            backgroundImage: `repeating-linear-gradient(to right, ${INK} 0, ${INK} 2px, transparent 2px, transparent 8px)`,
            opacity: 0.4,
          },
          '&::before': { top: 0 },
          '&::after': { bottom: 0 },
          // hide duplicate rule between stacked rows
          '& + &::before': { display: 'none' },
          '&:hover .verified-stamp': {
            opacity: 1,
            transform: 'rotate(-6deg) scale(1)',
          },
        }}
      >
        {/* Avatar — soft-squared, NOT circular */}
        <Box
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: '4px',
            overflow: 'hidden',
            border: `1px solid ${INK}`,
            bgcolor: CREAM_DEEP,
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src={`https://i.pravatar.cc/96?img=${avatarSeed}`}
            alt={handle}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'grayscale(12%) contrast(1.05)',
              display: 'block',
            }}
          />
          {/* tiny corner SKU tick */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              px: 0.5,
              py: '1px',
              bgcolor: INK,
              color: CREAM,
              fontFamily: MONO_STACK,
              fontSize: 8,
              letterSpacing: '0.12em',
              lineHeight: 1,
              borderTopRightRadius: '2px',
            }}
          >
            #{String(avatarSeed).padStart(2, '0')}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: MONO_STACK,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: INK,
              }}
            >
              {handle.replace('@', '')}
            </Typography>

            {/* Hover-reveal VERIFIED stamp */}
            <Box
              className="verified-stamp"
              sx={{
                opacity: 0,
                transform: 'rotate(-6deg) scale(0.92)',
                transition: 'opacity 0.25s ease, transform 0.25s ease',
                border: `1.5px solid ${INDIGO}`,
                color: INDIGO,
                fontFamily: MONO_STACK,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                px: 0.75,
                py: '2px',
                borderRadius: '2px',
                bgcolor: 'transparent',
                pointerEvents: 'none',
              }}
            >
              ✓ Verified
            </Box>
          </Box>

          <Typography
            component="p"
            sx={{
              fontFamily: INTER_STACK,
              fontSize: 14,
              lineHeight: 1.55,
              color: INK,
              opacity: 0.88,
              m: 0,
            }}
          >
            {quote}
          </Typography>

          {/* "price line" — mimics a receipt line item */}
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontFamily: MONO_STACK,
              fontSize: 10.5,
              letterSpacing: '0.14em',
              color: INK,
              opacity: 0.55,
              textTransform: 'uppercase',
            }}
          >
            <Box component="span">Review</Box>
            <Box
              sx={{
                flex: 1,
                height: '1px',
                backgroundImage: `repeating-linear-gradient(to right, ${INK} 0, ${INK} 2px, transparent 2px, transparent 6px)`,
                opacity: 0.5,
              }}
            />
            <Box component="span">5.00 ★</Box>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

/* ───────────────────── LeftPanel ─────────────────────
 * Cream panel with warehouse photo multiply-blended in, brandmark top-left,
 * headline + receipt-row testimonials, swing tags on right edge.
 * "Printing out" reveal handled by parent Login/Register via clipPath.
 */
export interface LeftPanelProps {
  headline: React.ReactNode;
  subtitle: string;
  testimonials: { handle: string; avatarSeed: number; quote: string }[];
  kicker: string;
  bgUrl: string;
  reducedMotion: boolean;
}

export const LeftPanel: React.FC<LeftPanelProps> = ({
  headline,
  subtitle,
  testimonials,
  kicker,
  bgUrl,
  reducedMotion,
}) => {
  // "Printing out" effect — clipPath reveals top-to-bottom over 800ms.
  const printVariants = reducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 } }
    : {
        initial: { clipPath: 'inset(0 0 100% 0)', opacity: 0 },
        animate: { clipPath: 'inset(0 0 0% 0)', opacity: 1 },
      };

  return (
    <Box
      component={motion.div}
      initial={printVariants.initial}
      animate={printVariants.animate}
      transition={{ duration: 0.8, ease: [0.22, 0.8, 0.2, 1] }}
      sx={{
        position: 'relative',
        width: '50%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: 0,
        p: { md: 5, lg: 7 },
        pr: { md: 8, lg: 10 }, // space for swing tags
        bgcolor: '#ffffff',
        color: INK,
        overflow: 'hidden',
        // Warehouse photo as a faint watermark on white — kept subtle so INK text reads cleanly.
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${bgUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.14,
          mixBlendMode: 'multiply',
          filter: 'saturate(0.55) contrast(1.02)',
          pointerEvents: 'none',
        },
        // subtle "receipt paper" noise overlay
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent 0,
            transparent 3px,
            rgba(15,23,42,0.025) 3px,
            rgba(15,23,42,0.025) 4px
          )`,
        },
      }}
    >
      {/* Content above overlays */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <BrandMark color={INK} size="md" />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          mt: { md: 6, lg: 8 },
          maxWidth: 520,
        }}
      >
        <Box sx={{ mb: 2.5 }}>
          <SkuKicker label={kicker} />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontFamily: INTER_STACK,
            fontSize: { md: 34, lg: 40 },
            fontWeight: 700,
            lineHeight: 1.12,
            letterSpacing: '-0.02em',
            color: INK,
            mb: 1.5,
          }}
        >
          {headline}
        </Typography>

        <Typography
          sx={{
            fontFamily: INTER_STACK,
            fontSize: 15,
            lineHeight: 1.55,
            color: INK,
            opacity: 0.7,
            mb: 3.5,
          }}
        >
          {subtitle}
        </Typography>

        <DottedRule label="Reviews · 2024-Q4" my={1} />

        <Stack>
          {testimonials.map((t, i) => (
            <TestimonialRow
              key={t.handle}
              handle={t.handle}
              avatarSeed={t.avatarSeed}
              quote={t.quote}
              index={i}
              reducedMotion={reducedMotion}
            />
          ))}
        </Stack>
      </Box>

      {/* Footer ticket tape — "tear off" line with tiny pricing summary */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          mt: 'auto',
          pt: 3,
        }}
      >
        <DottedRule opacity={0.4} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            pt: 1.5,
            fontFamily: MONO_STACK,
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: INK,
            opacity: 0.7,
          }}
        >
          <Box component="span">
            © {new Date().getFullYear()} · DashFlow
          </Box>
          <Box component="span">AR · LATAM · Multicanal</Box>
        </Box>
      </Box>

      <SwingTags />
    </Box>
  );
};

/* ───────────────────── HeadlineWithUnderline ─────────────────────
 * Splits a headline into parts and wraps one word with an indigo highlighter
 * underline (offset, ~10px thick, opacity 0.35) — like a bodega ballpoint mark.
 */
export interface HighlightedWordProps {
  children: React.ReactNode;
  color?: string;
}

export const HighlightedWord: React.FC<HighlightedWordProps> = ({
  children,
  color = INDIGO,
}) => (
  <Box
    component="span"
    sx={{
      position: 'relative',
      whiteSpace: 'nowrap',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '-2px',
        right: '-2px',
        bottom: '2px',
        height: '10px',
        bgcolor: color,
        opacity: 0.28,
        zIndex: -1,
        borderRadius: '1px',
        transform: 'skewX(-4deg)',
      },
    }}
  >
    {children}
  </Box>
);
